import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  ActivityIndicator,
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
  useWindowDimensions,
} from 'react-native';
import SafeAreaWrapper from '../../components/Reusables/SafeAreaWrapper';
import Typography from '../../components/Reusables/Typography';
import { useTheme, GTFonts } from '../../theme';
import { fs, s, v, iconSize } from '../../utils/libs';
import { shouldUseExpandedLayout } from '../../utils/layoutUtils';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import { clearDemoCheckoutSessionData } from '../../models/demo/orders';
import { resetCheckoutPaymentIntent } from '../../redux/checkoutSlice';

interface Props {
  navigation: any;
}

const DEMO_STEPS = [
  { step: 1, text: 'Select Item and Quantity.' },
  { step: 2, text: 'Review Order, Amount and Payment Mode.' },
  { step: 3, text: 'Print and Share Receipt.' },
];

const DemoIntroScreen: React.FC<Props> = ({ navigation }) => {
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const colors = theme.colors;
  const { width, height } = useWindowDimensions();
  const useExpandedLayout = shouldUseExpandedLayout(width, height);
  const styles = useMemo(
    () => createStyles(theme, useExpandedLayout),
    [theme, useExpandedLayout],
  );
  const sessionResetRef = useRef<Promise<void> | null>(null);
  const sessionResetDoneRef = useRef(false);
  const isStartingRef = useRef(false);
  const [waitingForReset, setWaitingForReset] = useState(false);

  // Every demo starts here, so a run that was quit or killed with the app is
  // wiped now; nothing runs at the moment the app is killed. This also opens
  // the demo database while the intro is read, so the product list doesn't wait
  // for PowerSync's setup.
  useEffect(() => {
    dispatch(resetCheckoutPaymentIntent());
    sessionResetRef.current = clearDemoCheckoutSessionData()
      .catch(error => {
        // The demo still opens; if the database failed, the product list retries.
        console.error('[DemoIntroScreen] Failed to reset demo session:', error);
      })
      .finally(() => {
        sessionResetDoneRef.current = true;
      });
  }, [dispatch]);

  const handleMakeDemoSale = useCallback(async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('demo_started', { ...ctx });
    // The basket loads once on open, so it must not read before the wipe ends.
    // The spinner shows only if this tap actually has to wait.
    if (!sessionResetDoneRef.current) {
      setWaitingForReset(true);
      await sessionResetRef.current;
    }
    navigation.replace('DemoProducts');
  }, [navigation]);

  const handleSkip = useCallback(() => {
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('demo_skipped', { ...ctx });
    appAnalytics.track('acq_create_account_tapped', {
      ...ctx,
      source: 'demo_skip',
    });
    // Navigate to Register in parent AuthStack
    navigation.getParent()?.navigate('Register');
  }, [navigation]);

  return (
    <SafeAreaWrapper
      edges={['top', 'left', 'right']}
      backgroundColor={theme.colors.surface}
      statusBarStyle="dark-content">
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          {/* Illustration Header */}
          <View style={styles.illustrationContainer}>
            <Image
              source={{
                uri: 'https://res.cloudinary.com/dbi34vmol/image/upload/f_webp,q_auto/v1754740115/samples/people/orderprocessing_gjiyxf.png',
              }}
              style={styles.illustration}
              resizeMode="contain"
            />
          </View>

          {/* Title */}
          <Typography style={styles.title}>
            Sales made simple: Create receipts in a few taps.
          </Typography>

          {/* Steps Timeline */}
          <View style={styles.stepsContainer}>
            {DEMO_STEPS.map((item, index) => (
              <View key={item.step} style={styles.stepRow}>
                {/* Step Number Circle */}
                <View style={styles.stepIndicatorColumn}>
                  <View
                    style={[
                      styles.stepCircle,
                      { backgroundColor: colors.primary },
                    ]}>
                    <Typography style={styles.stepNumber}>
                      {item.step}
                    </Typography>
                  </View>
                  {/* Connector Line (not on last item) */}
                  {index < DEMO_STEPS.length - 1 && (
                    <View
                      style={[
                        styles.stepLine,
                        { backgroundColor: colors.primary },
                      ]}
                    />
                  )}
                </View>

                {/* Step Text */}
                <View style={styles.stepTextContainer}>
                  <Typography style={styles.stepText}>{item.text}</Typography>
                </View>
              </View>
            ))}
          </View>
        </ScrollView>

        {/* Bottom CTA Section */}
        <View style={styles.ctaContainer}>
          <Typography style={styles.ctaDescription}>
            As Simple as 1, 2, 3!
          </Typography>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.success }]}
            onPress={handleMakeDemoSale}
            activeOpacity={0.8}
            testID="demo-start-button">
            {waitingForReset ? (
              <ActivityIndicator size="small" color={colors.textInverse} />
            ) : (
              <Typography style={styles.primaryButtonText}>
                MAKE A DEMO SALE!
              </Typography>
            )}
          </TouchableOpacity>

          <TouchableOpacity onPress={handleSkip} activeOpacity={0.7}>
            <Typography style={[styles.skipText, { color: colors.primary }]}>
              Skip & create my bizplus account
            </Typography>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaWrapper>
  );
};

const createStyles = (theme: any, useExpandedLayout: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      ...(useExpandedLayout && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: s(24),
      paddingBottom: v(20),
    },
    illustrationContainer: {
      alignItems: 'center',
    },
    illustration: {
      width: s(Platform.OS === 'web' ? 340 : 260),
      height: v(Platform.OS === 'web' ? 300 : 240),
    },
    title: {
      fontFamily: GTFonts.bold,
      fontSize: fs(24),
      lineHeight: fs(28),
      color: theme.colors.text,
      marginBottom: v(32),
    },
    stepsContainer: {
      paddingLeft: s(8),
    },
    stepRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    stepIndicatorColumn: {
      alignItems: 'center',
      marginRight: s(16),
    },
    stepCircle: {
      width: s(40),
      height: v(40),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepNumber: {
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      color: theme.colors.textInverse,
    },
    stepLine: {
      width: s(2),
      height: v(25),
      marginVertical: v(4),
    },
    stepTextContainer: {
      justifyContent: 'center',
      minHeight: v(40),
    },
    stepText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    ctaContainer: {
      paddingHorizontal: s(24),
      paddingTop: v(16),
      paddingBottom: v(32),
      backgroundColor: theme.colors.surface,
    },
    ctaDescription: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: v(16),
    },
    primaryButton: {
      width: '80%',
      alignSelf: 'center',
      height: v(48),
      borderRadius: s(8),
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: v(16),
    },
    primaryButtonText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(14),
      color: theme.colors.textInverse,
      // letterSpacing: 0.5,
    },
    skipText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
      textDecorationLine: 'underline',
    },
  });

export default DemoIntroScreen;
